import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, KeyRound } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { MessageRoutingCard } from "@/components/MessageRoutingCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const ROLE_LABEL = { super_admin: "Super admin", broker: "Broker", sales: "Sales", admin: "Super admin", staff: "Broker" };
const ROLE_STYLE = {
  super_admin: "bg-purple-100 text-purple-700", admin: "bg-purple-100 text-purple-700",
  broker: "bg-rose-100 text-rose-600", staff: "bg-rose-100 text-rose-600",
  sales: "bg-emerald-100 text-emerald-700",
};

export default function StaffTeam() {
  const { user } = useAuth();
  const isSuper = ["super_admin", "admin"].includes(user?.role);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: isSuper ? "broker" : "sales" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [activity, setActivity] = useState({ id: null, items: [] });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/team").then(({ data }) => setMembers(data)).catch(() => toast.error("Could not load team"));
  useEffect(() => { load(); }, []);

  const toggleActive = async (m) => {
    try { await api.patch(`/team/${m.user_id}/status`, { active: m.active === false }); toast.success(m.active === false ? "Reactivated" : "Suspended"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };
  const viewActivity = async (m) => {
    if (activity.id === m.user_id) { setActivity({ id: null, items: [] }); return; }
    try { const { data } = await api.get(`/team/activity?user_id=${m.user_id}`); setActivity({ id: m.user_id, items: data }); }
    catch { toast.error("Could not load activity"); }
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Fill in name, email and a temporary password"); return; }
    try {
      await api.post("/team", form);
      toast.success(`${ROLE_LABEL[form.role]} added`);
      setForm({ name: "", email: "", password: "", role: isSuper ? "broker" : "sales" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add member"); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email}? This deletes their login.`)) return;
    try { await api.delete(`/team/${m.user_id}`); toast.success("Member removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  const changeRole = async (m, role) => {
    try { await api.patch(`/team/${m.user_id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update role"); }
  };
  const changePassword = async () => {
    if (!pw.current_password || pw.new_password.length < 8) { toast.error("Enter your current password and a new one (8+ chars)"); return; }
    try { await api.post("/auth/change-password", pw); toast.success("Password updated"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change password"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8" data-testid="staff-team-page">
        <h1 className="font-display text-3xl font-black text-slate-900 dark:text-slate-100">Team</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{isSuper ? "Add brokers and sales reps, manage roles, and remove access." : "Add sales reps to your team."}</p>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <h2 className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">Members</h2>
            <div className="mt-4 space-y-2" data-testid="team-list">
              {members.length === 0 && <p className="text-sm text-slate-400 dark:text-slate-500">No team members yet.</p>}
              {members.map((m) => (
                <div key={m.user_id} data-testid={`team-member-${m.user_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-100 dark:border-slate-800 p-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{m.name || "—"} {m.is_self && <span className="text-xs text-slate-400 dark:text-slate-500">(you)</span>} {m.active === false && <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700" data-testid={`suspended-${m.user_id}`}>Suspended</span>}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{m.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${ROLE_STYLE[m.role] || "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>{ROLE_LABEL[m.role] || m.role}</span>
                      {isSuper && m.can_manage && (
                        <Select value={["broker", "sales"].includes(m.role) ? m.role : undefined} onValueChange={(v) => changeRole(m, v)}>
                          <SelectTrigger className="h-8 w-24 text-xs" data-testid={`role-select-${m.user_id}`}><SelectValue placeholder="Role" /></SelectTrigger>
                          <SelectContent><SelectItem value="broker">Broker</SelectItem><SelectItem value="sales">Sales</SelectItem></SelectContent>
                        </Select>
                      )}
                      <Button variant="ghost" size="sm" className="text-xs text-slate-500 dark:text-slate-400" onClick={() => viewActivity(m)} data-testid={`activity-${m.user_id}`}>Activity</Button>
                      {m.can_manage && !m.is_self && (
                        <>
                          <Button variant="ghost" size="sm" className={m.active === false ? "text-emerald-600" : "text-amber-600"} onClick={() => toggleActive(m)} data-testid={`toggle-active-${m.user_id}`}>{m.active === false ? "Reactivate" : "Suspend"}</Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(m)} data-testid={`remove-${m.user_id}`}><Trash2 className="h-4 w-4" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                  {activity.id === m.user_id && (
                    <div className="ml-3 mt-1 space-y-1 border-l-2 border-slate-100 dark:border-slate-800 pl-3" data-testid={`activity-list-${m.user_id}`}>
                      {activity.items.length === 0 ? <p className="text-xs text-slate-400 dark:text-slate-500 py-2">No recorded activity yet.</p> : activity.items.map((a, i) => (
                        <div key={i} className="py-1 text-xs text-slate-600 dark:text-slate-300">
                          <span className="font-semibold text-slate-800 dark:text-slate-200">{a.action.replace(/_/g, " ")}</span> {a.vehicle && `· ${a.vehicle}`} {a.detail && <span className="text-slate-400 dark:text-slate-500">— {a.detail}</span>}
                          <span className="ml-1 text-slate-400 dark:text-slate-500">{a.at ? new Date(a.at).toLocaleDateString() : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900 dark:text-slate-100"><UserPlus className="h-5 w-5 text-rose-600" /> Add member</h2>
              <div className="mt-4 space-y-3">
                <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Full name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="new-member-name" /></div>
                <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="new-member-email" /></div>
                <div><Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Temporary password</Label><Input value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" placeholder="8+ characters" data-testid="new-member-password" /></div>
                <div>
                  <Label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400">Role</Label>
                  <Select value={form.role} onValueChange={(v) => set("role", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="new-member-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {isSuper && <SelectItem value="broker">Broker</SelectItem>}
                      <SelectItem value="sales">Sales</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={create} className="w-full rounded-md bg-slate-950 font-semibold hover:bg-slate-800" data-testid="add-member-btn">Add member</Button>
                <p className="text-xs text-slate-400 dark:text-slate-500">They log in with this email + temporary password and can change it from here.</p>
              </div>
            </div>

            <MessageRoutingCard />

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900 dark:text-slate-100"><KeyRound className="h-5 w-5 text-slate-700 dark:text-slate-300" /> Change my password</h2>
              <div className="mt-4 space-y-3">
                <Input type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} placeholder="Current password" data-testid="cur-password" />
                <Input type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} placeholder="New password (8+ chars)" data-testid="new-password" />
                <Button onClick={changePassword} variant="outline" className="w-full rounded-md font-semibold" data-testid="change-password-btn">Update password</Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
